"use client";

import { useEffect, useState } from "react";

import { isDevEnvironment } from "@/lib/env";

/**
 * Reactively tracks the admin env preview state, which is stored as
 * `<html data-env="dev|prod">` by `EnvPreviewToggle`. Components that
 * render env-specific *content* (like the MCP URL) use this so they
 * swap when the admin flips the toggle — CSS gates alone can only hide
 * elements, not change their text.
 *
 * Defaults to the real environment (`isDevEnvironment()`) before the
 * toggle's useEffect has run, so non-admin users see correct content.
 */
export function useEnvPreview(): "dev" | "prod" {
  const [env, setEnv] = useState<"dev" | "prod">(() =>
    isDevEnvironment() ? "dev" : "prod"
  );

  useEffect(() => {
    const html = document.documentElement;
    const read = () => {
      const attr = html.getAttribute("data-env");
      if (attr === "prod" || attr === "dev") {
        setEnv(attr);
      }
    };
    read();
    const observer = new MutationObserver(read);
    observer.observe(html, { attributes: true, attributeFilter: ["data-env"] });
    return () => observer.disconnect();
  }, []);

  return env;
}
